import {lazy, memo, Suspense, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS, BADGE_SIZES} from '@adv-mt/ui/badge'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Body1, Body2, Subhead1} from '@adv-mt/ui/typography'

import {BASE_CLASS as PAGE_BASE_CLASS} from '../../config.js'
import {RESERVATION_DRAWER_CONTENTS} from '../../hooks/useReservationHash.js'
import {getInfoByReservationStatus, RESERVATION_PLACEMENTS} from './config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-reservation`

// Kept out of the page bundle and out of the DS drawer stack in `../../drawers`: this drawer
// portals itself into document.body and owns its own overlay, so it can't be a child of the
// shared `Drawer` root. Same chunk name as the legacy detail's entry point, so both share it.
const LazyDrawerReservationInitialization = lazy(() =>
  import(/* webpackChunkName: "LazyDrawerReservationInitialization" */ '@adv-mt/mt-drawer-reservation-initialization')
)

const Reservation = ({
  ad = {},
  financingData,
  openDrawerContentType = null,
  placement,
  reservationNewFlow = {},
  seller = {}
}) => {
  const {domain, i18n, user} = useSuiContext()
  const {track} = useAnalyticsTrack()
  const router = useRouter()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerContentType, setDrawerContentType] = useState(RESERVATION_DRAWER_CONTENTS.INFO)

  // Reservation/Init hashes: the page hands down which content to show, only to the visible placement.
  useEffect(() => {
    if (openDrawerContentType === null) return
    setDrawerContentType(openDrawerContentType)
    setIsDrawerOpen(true)
  }, [openDrawerContentType])

  const config = domain.get('config')
  const {RESERVATION_STATUSES} = config.get('financing')
  const {HIT_ORIGINS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const infoByReservationStatus = getInfoByReservationStatus(RESERVATION_STATUSES)
  const isReservable = reservationNewFlow.adReservationStatus === RESERVATION_STATUSES.RESERVABLE
  const statusInfo = infoByReservationStatus[reservationNewFlow.adReservationStatus]
  const rootClassName = cx(BASE_CLASS, `${BASE_CLASS}--${placement}`)

  const adInfo = {
    dealerName: seller.dealer?.name || '',
    dealerRating: seller.dealer?.ratings || null,
    id: ad.id,
    images: ad.photos || [],
    imageUrl: ad.imgUrl || '',
    instalment: financingData?.fee || null,
    price: ad.price || null,
    title: ad.title || ''
  }

  const handleOpenDrawer = async () => {
    setDrawerContentType(RESERVATION_DRAWER_CONTENTS.INFO)
    setIsDrawerOpen(true)

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: TRACKING_EVENT_NAMES.RESERVATION_INFORMATION_CLICKED,
      origin: HIT_ORIGINS.AD_DETAIL
    })
    track(eventData)
  }

  const handleManageReservation = async () => {
    const [error, response] = await domain.get('get_messaging_conversation_id_user_use_case').execute({
      entityId: ad.id,
      buyerId: user?.userId,
      sellerId: seller.dealer?.id,
      isProfessionalSeller: seller.isDealer
    })

    if (error !== null) {
      router.push({pathname: '/user/messages'})
      return
    }

    router.push({pathname: `/user/messages/#/id/${response?.conversationId}`})
  }

  if (isReservable) {
    return (
      <section className={rootClassName}>
        <div className={`${BASE_CLASS}-main`}>
          <Badge design={BADGE_DESIGNS.SOFT} intent={BADGE_INTENTS.INFO} size={BADGE_SIZES.MEDIUM}>
            {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.BADGE')}
          </Badge>
          <div className={`${BASE_CLASS}-text`}>
            <Subhead1 as="p" className={`${BASE_CLASS}-textTitle`} highlight>
              {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.TITLE_EXPERIMENT')}
            </Subhead1>
            <div className={`${BASE_CLASS}-subtitles`}>
              <Body2 className={`${BASE_CLASS}-subtitlesText`}>
                {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT')}
              </Body2>
              <Body2>
                <span className={`${BASE_CLASS}-refund`}>
                  {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT_REFUND')}
                </span>
                {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT_REFUND_SUFFIX')}
              </Body2>
            </div>
          </div>
        </div>
        <Button
          fullWidth
          onClick={handleOpenDrawer}
          shape={BUTTON_SHAPES.CIRCLE}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.OUTLINE}
          className={`${BASE_CLASS}-cta`}
        >
          {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.CTA_EXPERIMENT')}
        </Button>
        {isDrawerOpen && (
          <Suspense fallback={null}>
            <LazyDrawerReservationInitialization
              adInfo={adInfo}
              drawerContent={drawerContentType}
              hitOrigin={HIT_ORIGINS.AD_DETAIL}
              isDrawerOpen={isDrawerOpen}
              onCloseDrawer={() => setIsDrawerOpen(false)}
              isNewReservationModuleV3
            />
          </Suspense>
        )}
      </section>
    )
  }

  if (statusInfo === undefined) {
    return null
  }

  const {Icon: StatusBadgeIcon, intent: statusBadgeIntent, text: statusBadgeText} = statusInfo.badge

  return (
    <section className={rootClassName}>
      <div className={`${BASE_CLASS}-main`}>
        <Badge
          design={BADGE_DESIGNS.SOFT}
          iconLeft={<StatusBadgeIcon size={atomIconSizes.small} />}
          intent={statusBadgeIntent}
          size={BADGE_SIZES.MEDIUM}
        >
          {i18n.t(statusBadgeText)}
        </Badge>
        <div className={`${BASE_CLASS}-text`}>
          <Body1 highlight>{i18n.t(statusInfo.title)}</Body1>
          {statusInfo.text !== null && <Body1>{i18n.t(statusInfo.text)}</Body1>}
        </div>
      </div>
      {statusInfo.hasManageButton && (
        <Button
          fullWidth
          onClick={handleManageReservation}
          shape={BUTTON_SHAPES.CIRCLE}
          size={BUTTON_SIZES.MEDIUM}
          variant={BUTTON_VARIANTS.OUTLINE}
          className={`${BASE_CLASS}-cta`}
        >
          {i18n.t('RESERVATION_C2B.CARD.BUTTON_TEXT_MANAGE_RESERVATION')}
        </Button>
      )}
    </section>
  )
}

Reservation.displayName = 'Reservation'

Reservation.propTypes = {
  ad: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    imgUrl: PropTypes.string,
    price: PropTypes.number,
    title: PropTypes.string
  }),
  financingData: PropTypes.shape({fee: PropTypes.number}),
  openDrawerContentType: PropTypes.oneOf(Object.values(RESERVATION_DRAWER_CONTENTS)),
  placement: PropTypes.oneOf(Object.values(RESERVATION_PLACEMENTS)).isRequired,
  reservationNewFlow: PropTypes.shape({adReservationStatus: PropTypes.string}),
  seller: PropTypes.shape({
    dealer: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      location: PropTypes.shape({province: PropTypes.string}),
      name: PropTypes.string,
      ratings: PropTypes.object
    }),
    isDealer: PropTypes.bool
  })
}

export {RESERVATION_PLACEMENTS}

export default memo(Reservation)
